import { LRLanguage, LanguageSupport } from '@codemirror/language';
import { parseMixed } from '@lezer/common';
import type { SyntaxNodeRef } from '@lezer/common';
import { parser } from './parser';
import { parser as multipartParser } from './multipart-parser';
import { json, jsonLanguage } from '@codemirror/lang-json';
import { javascript, javascriptLanguage } from '@codemirror/lang-javascript';
import { html, htmlLanguage } from '@codemirror/lang-html';
import { xml, xmlLanguage } from '@codemirror/lang-xml';
import { css, cssLanguage } from '@codemirror/lang-css';
import { python, pythonLanguage } from '@codemirror/lang-python';
import type { Extension } from '@codemirror/state';
import type { HttpBodyLanguage } from '../types';
import {
  isRawGraphQlRequestContentType,
  looksLikeGraphQlContent,
} from '../../../graphql';

// Create a language definition for multipart content
const multipartLanguage = LRLanguage.define({
  name: 'multipart',
  parser: multipartParser,
  languageData: {
    commentTokens: {},
  },
});

const languageCache = new Map<HttpBodyLanguage, LRLanguage>();

function getNestedParser(language: HttpBodyLanguage) {
  switch (language) {
    case 'json':
      return jsonLanguage.parser;
    case 'javascript':
      return javascriptLanguage.parser;
    case 'html':
      return htmlLanguage.parser;
    case 'xml':
      return xmlLanguage.parser;
    case 'css':
      return cssLanguage.parser;
    case 'python':
      return pythonLanguage.parser;
    case 'multipart':
      return multipartLanguage.parser;
    default:
      return null;
  }
}

function getNestedLanguageExtension(
  language: HttpBodyLanguage,
): Extension | null {
  switch (language) {
    case 'json':
      return json();
    case 'javascript':
      return javascript({ jsx: true, typescript: true });
    case 'html':
      return html();
    case 'xml':
      return xml();
    case 'css':
      return css();
    case 'python':
      return python();
    case 'multipart':
      return new LanguageSupport(multipartLanguage);
    default:
      return null;
  }
}

function getCachedLanguage(bodyLanguage: HttpBodyLanguage): LRLanguage {
  const cached = languageCache.get(bodyLanguage);
  if (cached) return cached;

  const nestedParser = getNestedParser(bodyLanguage);

  const mixedParser = nestedParser
    ? parser.configure({
        wrap: parseMixed((node: SyntaxNodeRef) => {
          if (node.name === 'BodyText') {
            return { parser: nestedParser };
          }
          return null;
        }),
      })
    : parser;

  const language = LRLanguage.define({
    name: 'http',
    parser: mixedParser,
    languageData: {
      commentTokens: {},
    },
  });

  languageCache.set(bodyLanguage, language);
  return language;
}

const HTTP_METHODS = [
  'GET',
  'POST',
  'PUT',
  'DELETE',
  'PATCH',
  'HEAD',
  'OPTIONS',
  'CONNECT',
  'TRACE',
];
const HTTP_VERSION_PATTERN = /^HTTP\/\d\.\d/;

function extractBodyFromHttpDocument(doc: string): string {
  const separatorMatch = doc.match(/\r?\n\r?\n/);
  if (!separatorMatch || separatorMatch.index === undefined) {
    return '';
  }

  return doc.slice(separatorMatch.index + separatorMatch[0].length).trim();
}

export function isValidHttpContent(doc: string): boolean {
  if (!doc || doc.length === 0) return false;

  const firstLine = doc.split('\n')[0]?.trim() || '';

  const isRequest = HTTP_METHODS.some((method) =>
    firstLine.startsWith(method + ' '),
  );
  const isResponse = HTTP_VERSION_PATTERN.test(firstLine);

  return isRequest || isResponse;
}

export function isHexContent(doc: string): boolean {
  if (!doc || doc.length < 10) return false;

  const lines = doc.split('\n').slice(0, 3);
  const hexLinePattern = /^[0-9a-fA-F]{8}\s+(?:[0-9a-fA-F]{2}\s+)+/;

  return lines.some((line) => hexLinePattern.test(line));
}

export function detectContentType(doc: string): HttpBodyLanguage {
  const contentTypeMatch = doc.match(/content-type:\s*([^\r\n;]+)/i);
  const body = extractBodyFromHttpDocument(doc);

  if (contentTypeMatch) {
    const contentType = contentTypeMatch[1].toLowerCase().trim();

    if (isRawGraphQlRequestContentType(contentType)) {
      return 'text';
    }

    if (contentType.includes('json') || contentType.includes('+json')) {
      if (body) {
        try {
          JSON.parse(body);
        } catch {
          if (looksLikeGraphQlContent(body)) {
            return 'text';
          }
        }
      }
      return 'json';
    }
    if (
      contentType.includes('javascript') ||
      contentType.includes('ecmascript')
    )
      return 'javascript';
    if (contentType.includes('html')) return 'html';
    if (contentType.includes('xml') || contentType.includes('+xml'))
      return 'xml';
    if (contentType.includes('css')) return 'css';
    if (contentType.includes('x-www-form-urlencoded')) return 'form-urlencoded';
    if (contentType.includes('multipart')) return 'multipart';
  }

  // No content-type header — sniff the body
  if (body) {
    const trimmed = body.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        JSON.parse(trimmed);
        return 'json';
      } catch {
        // Not valid JSON despite looking like it
      }
    }
    if (trimmed.startsWith('<?xml') || trimmed.startsWith('<!DOCTYPE')) {
      return 'xml';
    }
    if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
      return 'html';
    }
  }

  return 'text';
}

export function createHttpLanguage(doc: string): LRLanguage {
  const bodyLanguage = detectContentType(doc);
  return getCachedLanguage(bodyLanguage);
}

export function httpLanguageSupport(doc: string): LanguageSupport {
  const bodyLanguage = detectContentType(doc);
  const httpLang = getCachedLanguage(bodyLanguage);
  const nestedExtension = getNestedLanguageExtension(bodyLanguage);

  if (nestedExtension) {
    return new LanguageSupport(httpLang, [nestedExtension]);
  }

  return new LanguageSupport(httpLang);
}

export function createHttpExtensions(doc: string): Extension[] {
  if (!isValidHttpContent(doc) || isHexContent(doc)) {
    return [];
  }

  return [httpLanguageSupport(doc)];
}

/**
 * Get a standalone language extension for a body language (no HTTP wrapper).
 * Used for editors showing just a fragment like a GraphQL query or JSON variables.
 */
export function standaloneLanguageExtension(
  language: HttpBodyLanguage,
): Extension | null {
  return getNestedLanguageExtension(language);
}

export function clearLanguageCaches(): void {
  languageCache.clear();
}
